import { useCallback, useEffect, useMemo, useState } from "react"
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { useEventReview } from "./EventReviewProvider"
import { currentlyActiveVisualizationAreaAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Visualizations"
import { selectedLayoutAtom } from "../Pages/Data/Visualize/DataReview/Atoms/Layout"
import {
	getTimelineControllerWindowId,
	isAnnotationPlaythroughController,
	linkedWindowsTimelineControllerAtom,
	timelinePlaybackCommandAtom,
	timelineViewRangesAtom,
} from "../Pages/Data/Visualize/DataReview/Atoms/Timeline"
import { VisualizationComponent } from "../Pages/Data/Visualize/DataReview/VisualizationComponent"

const PLAYABLE_COMPONENTS = new Set<string>([
    VisualizationComponent.TIME_SERIES_GROUP,
    VisualizationComponent.EEG_MONTAGE,
    VisualizationComponent.PERSYST_EEG,
    VisualizationComponent.PERSYST_TRENDS,
    VisualizationComponent.SD_DETECTION,
    VisualizationComponent.MAPOPT_PLOT,
    VisualizationComponent.CPPOPT_PLOT,
    VisualizationComponent.HISTOGRAM,
    VisualizationComponent.SCATTER_PLOT,
])

const createCommandId = () => Date.now() * 1000 + Math.floor(Math.random() * 1000)

const CONTROLLER_ACQUIRE_TIMEOUT_MS = 1500

type UsePlayThroughAnnotationReturn = {
    start: () => void
    stop: () => void
    toggle: () => void
    isPlaying: boolean
    canPlay: boolean
}

type ActivePlayThrough = {
    requestId: number
    targetWindowId: string
    annotationId: number
    annotationEndTime: number
    annotationStartTime: number
    requestedAt: number
    hasController: boolean
}

export const usePlayThroughAnnotation = (): UsePlayThroughAnnotationReturn => {
    const eventReview = useEventReview()
    const selectedLayout = useRecoilValue(selectedLayoutAtom)
    const activeWindowId = useRecoilValue(currentlyActiveVisualizationAreaAtom)
    const [linkedTimelineController, setLinkedTimelineController] = useRecoilState(linkedWindowsTimelineControllerAtom)
    const setPlaybackCommand = useSetRecoilState(timelinePlaybackCommandAtom)
    const getTimelineViewRanges = useRecoilCallback(({ snapshot }) => () => {
        return snapshot.getLoadable(timelineViewRangesAtom).getValue()
    }, [])

    const [activeRequest, setActiveRequest] = useState<ActivePlayThrough | null>(null)

    const playableWindowId = useMemo(() => {
        if (!selectedLayout) {
            return null
        }

        const areas = selectedLayout.areas ?? []

        const findPlayable = (areaId: string | undefined) => {
            if (!areaId) {
                return null
            }

            const found = areas.find(area => area.area === areaId && PLAYABLE_COMPONENTS.has(area.componentId))
            return found?.area ?? null
        }

        const eegWindow = areas.find(area => area.componentId === VisualizationComponent.EEG_MONTAGE && PLAYABLE_COMPONENTS.has(area.componentId))
        if (eegWindow) {
            return eegWindow.area
        }

        const activePlayable = findPlayable(activeWindowId)

        if (activePlayable) {
            return activePlayable
        }

        const fallback = areas.find(area => PLAYABLE_COMPONENTS.has(area.componentId))
        return fallback?.area ?? null
    }, [activeWindowId, selectedLayout])

    const linkedTimelineControllerWindowId = useMemo(() => getTimelineControllerWindowId(linkedTimelineController), [linkedTimelineController])
    const annotationPlaythroughController = useMemo(
        () => (isAnnotationPlaythroughController(linkedTimelineController) ? linkedTimelineController : null),
        [linkedTimelineController],
    )
    const currentAnnotation = eventReview.currentAnnotation
    const annotationDuration = currentAnnotation ? (Math.max((currentAnnotation.end_time ?? currentAnnotation.start_time) - currentAnnotation.start_time, 0)) : 0
    const canPlay = Boolean(
        currentAnnotation
        && playableWindowId
        && annotationDuration > 0
        && (!linkedTimelineControllerWindowId || linkedTimelineControllerWindowId === playableWindowId),
    )

    const clearPlaybackCommandForRequest = useCallback(
        (requestId: number) => {
            setPlaybackCommand(previous => (previous && previous.id === requestId ? null : previous))
        },
        [setPlaybackCommand],
    )

    const start = useCallback(() => {
        if (!currentAnnotation || !playableWindowId || activeRequest) {
            return
        }

        if (linkedTimelineControllerWindowId && linkedTimelineControllerWindowId !== playableWindowId) {
            return
        }

        const viewRanges = getTimelineViewRanges()
        const currentViewRange = viewRanges[playableWindowId]

        if (!currentViewRange) {
            return
        }

        const annotationStart = currentAnnotation.start_time
        const annotationEnd = currentAnnotation.end_time ?? currentAnnotation.start_time

        if (annotationEnd <= annotationStart) {
            return
        }

        const commandId = createCommandId()

        setActiveRequest({
            requestId: commandId,
            targetWindowId: playableWindowId,
            annotationId: currentAnnotation.id,
            annotationEndTime: annotationEnd,
            annotationStartTime: annotationStart,
            requestedAt: Date.now(),
            hasController: false,
        })

        setPlaybackCommand({
            id: commandId,
            action: "playThrough",
            targetWindowId: playableWindowId,
            requestId: commandId,
            payload: { annotationStart, annotationEnd },
        })
    }, [activeRequest, currentAnnotation, getTimelineViewRanges, linkedTimelineControllerWindowId, playableWindowId, setPlaybackCommand])

    const stop = useCallback(() => {
        const requestId = activeRequest?.requestId ?? annotationPlaythroughController?.requestId
        const targetWindowId = activeRequest?.targetWindowId ?? annotationPlaythroughController?.windowId

        if (!requestId || !targetWindowId) {
            return
        }

        setPlaybackCommand({
            id: createCommandId(),
            action: "pause",
            targetWindowId,
            requestId,
        })

        clearPlaybackCommandForRequest(requestId)
        setLinkedTimelineController(previous => {
            const previousRequestId = isAnnotationPlaythroughController(previous) ? previous.requestId : undefined
            const previousControllerWindowId = getTimelineControllerWindowId(previous)
            if (previousRequestId !== undefined && previousRequestId !== requestId) {
                return previous
            }

            if (previousControllerWindowId !== targetWindowId) {
                return previous
            }

            return null
        })
        setActiveRequest(null)
    }, [activeRequest, annotationPlaythroughController, clearPlaybackCommandForRequest, setLinkedTimelineController, setPlaybackCommand])

    const toggle = useCallback(() => {
        if (activeRequest || annotationPlaythroughController) {
            stop()
            return
        }

        start()
    }, [activeRequest, annotationPlaythroughController, start, stop])

    useEffect(() => {
        if (!activeRequest) {
            return
        }

        if (!activeRequest.hasController && linkedTimelineControllerWindowId === activeRequest.targetWindowId) {
            setLinkedTimelineController(previous => {
                const previousControllerWindowId = getTimelineControllerWindowId(previous)
                if (previousControllerWindowId !== activeRequest.targetWindowId) {
                    return previous
                }

                if (isAnnotationPlaythroughController(previous) && previous.requestId === activeRequest.requestId) {
                    return previous
                }

                return {
                    windowId: activeRequest.targetWindowId,
                    mode: "annotationPlaythrough",
                    requestId: activeRequest.requestId,
                }
            })
            setActiveRequest(previous => (previous ? { ...previous, hasController: true } : previous))
            clearPlaybackCommandForRequest(activeRequest.requestId)
            return
        }

        if (activeRequest.hasController && linkedTimelineControllerWindowId !== activeRequest.targetWindowId) {
            clearPlaybackCommandForRequest(activeRequest.requestId)
            setActiveRequest(null)
        }
    }, [activeRequest, clearPlaybackCommandForRequest, linkedTimelineControllerWindowId, setLinkedTimelineController])

    useEffect(() => {
        if (!activeRequest || activeRequest.hasController) {
            return
        }

        const timeout = setTimeout(() => {
            clearPlaybackCommandForRequest(activeRequest.requestId)
            setActiveRequest(null)
        }, CONTROLLER_ACQUIRE_TIMEOUT_MS)

        return () => clearTimeout(timeout)
    }, [activeRequest, clearPlaybackCommandForRequest])

    useEffect(() => {
        if (!activeRequest) {
            return
        }

        const currentAnnotationEnd = currentAnnotation?.end_time ?? currentAnnotation?.start_time
        if (
            !currentAnnotation
            || currentAnnotation.id !== activeRequest.annotationId
            || currentAnnotation.start_time !== activeRequest.annotationStartTime
            || currentAnnotationEnd !== activeRequest.annotationEndTime
        ) {
            stop()
        }
    }, [activeRequest, currentAnnotation, stop])

    return {
        start,
        stop,
        toggle,
        isPlaying: activeRequest !== null || annotationPlaythroughController !== null,
        canPlay,
    }
}
